/**
 * GIC-specific project picker field configuration.
 * Contains GIC-specific transformers and scopes that override/extend common behavior.
 */

import type { LookupProject } from '@atlassian/ui-modifications-core/types';
import { baseScopesRequiredPerField } from '@atlassian/ui-modifications-core/constants';
import { FieldValueLookupError } from '@atlassian/ui-modifications-core/utils';
import { singleValueLookup } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/single-value-lookup/index.tsx';
import type {
	ProjectPickerPublicSetValue,
	ProjectPickerPublicGetValue,
} from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/project-picker.tsx';

/**
 * GIC-specific internal format - matches UI Option format for simplicity.
 * Like assignee field, internal = public format.
 */
export type ProjectPickerGICInternalValue = null | {
	/** Display label (project name) */
	label: string;
	/** Project key */
	value: string;
	/** Avatar icon URL */
	iconUrl: string;
	/** Project ID (numeric format for GIC submission) */
	projectId: string;
};

/**
 * GIC-specific setValue transformer.
 * Converts numeric project ID to GIC UI option format.
 *
 * GraphQL lookup provides: {id: "10004", key: "KEY", name: "Name", avatarUrls: {...}}
 * Output: {label: name, value: key, iconUrl: url, projectId: id}
 *
 * @param value - Numeric project ID (e.g., "10004") or null to clear
 * @param lookupValues - GraphQL lookup data with project details
 * @returns GIC UI option format, or null if clearing
 * @throws {FieldValueLookupError} If project not found in lookup
 *
 * @example
 * // Input: "10004"
 * // Output: {label: "My Project", value: "PROJ", iconUrl: "https://...", projectId: "10004"}
 */
export const projectPickerGICSetValueTransformer = (
	value: ProjectPickerPublicSetValue,
	lookupValues?: LookupProject[],
): ProjectPickerGICInternalValue => {
	if (value === null) {
		return null;
	}

	// Lookup project by numeric id field
	// GraphQL provides: {id: "10004", key: "KEY", name: "Name", avatarUrls: {...}}
	const foundProject = singleValueLookup<LookupProject>(value, lookupValues, 'id');

	if (!foundProject) {
		throw new FieldValueLookupError();
	}

	// Convert to GIC UI option format
	return {
		label: foundProject.name,
		value: foundProject.key,
		iconUrl: foundProject.avatarUrls['48x48'],
		projectId: foundProject.id, // Numeric ID for GIC
	};
};

/**
 * GIC-specific getValue transformer.
 * Extracts only projectId from GIC UI format to match Forge API contract.
 *
 * Internal has: {label: "Project", value: "KEY", iconUrl: "...", projectId: "10004"}
 * Public returns: {projectId: "10004"}
 *
 * @param value - GIC UI option format, or null
 * @returns Public format with only numeric projectId, or null
 *
 * @example
 * // Input: {label: "My Project", value: "PROJ", iconUrl: "...", projectId: "10004"}
 * // Output: {projectId: "10004"}
 */
export const projectPickerGICGetValueTransformer = (
	value: ProjectPickerGICInternalValue,
): ProjectPickerPublicGetValue => {
	// Truthy check handles both null and undefined (consistent with other transformers)
	if (!value) {
		return null;
	}
	// Extract only projectId for public API, following assignee pattern
	return { projectId: value.projectId };
};

export const projectPickerGicScopes = {
	...baseScopesRequiredPerField,
	granular: [...baseScopesRequiredPerField.granular, 'write:field.default-value:jira'],
};
